import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';

import './Appointments.css';

import {
  Calendar,
  Clock,
  Stethoscope,
  Check,
  Loader2,
  XCircle,
  MapPin,
  Video,
  Phone,
  Search,
  X,
  UserCheck
} from 'lucide-react';
import config from '../../../config';
import { useSelector } from 'react-redux';
import { formatPhoneDisplay } from '../../../utils/countryCodes';


// ============================================================
// CONFIG
// ============================================================

const TABS = [
  'Book appointment',
  'My appointments'
];

const APPOINTMENT_TYPES = [
  {
    value: 'physical',
    label: 'In-person visit',
    icon: MapPin
  },
  {
    value: 'zoom',
    label: 'Zoom video call',
    icon: Video
  },
  {
    // Direct phone call — the doctor rings the patient on the number
    // saved in their profile at the booked time. No link is generated.
    value: 'phone',
    label: 'Phone call',
    icon: Phone
  }
];


// ============================================================
// GET TOKEN
// ============================================================

// The login token is in localStorage ("remember me") or sessionStorage
// (normal login) — check both.
function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}


// ============================================================
// API REQUEST
// ============================================================

async function apiRequest(
  path,
  options = {},
  full = false        // true -> return the whole JSON body, not just .data
) {
  const token = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  // Send JWT with every protected request.
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${config.baseURL}/api${path}`, {
    ...options,
    headers
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(
      body.error ||
      body.message ||
      `Request failed (${res.status})`
    );
  }

  if (res.status === 204) {
    return null;
  }

  const json = await res.json();
  return full ? json : json.data;
}


// ============================================================
// API FUNCTIONS
// ============================================================

const fetchDoctors = () =>
  apiRequest('/doctors');

// The patient's timezone (worked out by the server from their profile
// country) and the next 7 calendar days in it.
const fetchCalendar = () =>
  apiRequest('/appointments/calendar');

// Open slots for ONE DAY of the patient's calendar. The server returns each
// slot already converted to the patient's timezone.
const fetchSlots = (doctorId, date) =>
  apiRequest(
    `/appointments/slots?doctorId=${doctorId}&date=${date}`,
    {},
    true
  );

const fetchAppointments = () =>
  apiRequest('/appointments');

const bookAppointment = (doctorId, slotId, appointmentType) =>
  apiRequest('/appointments', {
    method: 'POST',
    // NO PATIENT ID HERE — the server takes it from the token
    body: JSON.stringify({
      doctorId,
      slotId,
      appointmentType
    })
  });

const cancelAppointment = (id) =>
  apiRequest(`/appointments/${id}`, {
    method: 'DELETE'
  });


// ============================================================
// HELPERS
// ============================================================

function formatDate(dateStr) {
  const datePart = String(dateStr).slice(0, 10);

  return new Date(`${datePart}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

function formatTime(timeStr) {
  const [h, m] = timeStr.split(':');
  const hour = ((+h + 11) % 12) + 1;
  const ampm = +h < 12 ? 'AM' : 'PM';
  return `${hour}:${m} ${ampm}`;
}

function formatLocation(address, country) {
  return [address, country]
    .filter(Boolean)
    .join(', ');
}

// "Dr. Rashmi Gulati" / "Dr Gulati" -> "RG" / "G"
function doctorInitials(name) {
  return String(name || '?')
    .replace(/^dr\.?\s+/i, '')
    .split(' ')
    .filter(Boolean)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';
}

// The number a doctor would ring for a phone appointment: the logged-in
// patient's saved number, shown with its country code (e.g. "+91 8978787787").
// '' when they have no number on file.
function useMyPhoneDisplay() {
  const userInfo = useSelector(state => state.user?.userInfo);

  return formatPhoneDisplay({
    phone: userInfo?.phone,
    phoneCountryCode: userInfo?.phone_country_code,
    country: userInfo?.country
  });
}

function appointmentTypeLabel(type) {
  const match = APPOINTMENT_TYPES.find(t => t.value === type);
  return match ? match.label : 'In-person visit';
}


// ============================================================
// DOCTOR AVATAR — photo if the doctor has one, else initials
// ============================================================

function DoctorAvatar({ doctor }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [doctor.photo_url]);

  if (doctor.photo_url && !failed) {
    return (
      <div className="doctor-avatar" style={{ padding: 0, overflow: 'hidden' }}>
        <img
          src={doctor.photo_url}
          alt={doctor.name}
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '50%' }}
        />
      </div>
    );
  }

  return (
    <div className="doctor-avatar">
      {doctorInitials(doctor.name)}
    </div>
  );
}


// ============================================================
// DOCTOR CARD
// ============================================================

function DoctorCard({ doctor, selected, onClick }) {
  const location = formatLocation(doctor.address, doctor.country);

  return (
    <button
      onClick={onClick}
      className={`doctor-card ${selected ? 'selected' : ''}`}
      aria-expanded={selected}
    >
      <DoctorAvatar doctor={doctor} />

      <div className="doctor-info">
        <p className="doctor-name">
          {doctor.name}
        </p>

        <p className="doctor-specialty">
          {doctor.specialty}
        </p>

        {location && (
          <p className="doctor-bio" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <MapPin size={12} style={{ flexShrink: 0 }} />
            {location}
          </p>
        )}

        {doctor.bio && (
          <p className="doctor-bio">
            {doctor.bio}
          </p>
        )}
      </div>

      {selected && (
        <Check size={16} className="doctor-selected-check" />
      )}
    </button>
  );
}


// ============================================================
// DOCTOR SEARCH
// ============================================================

function DoctorSearch({ value, onChange }) {
  return (
    <div style={{ position: 'relative', maxWidth: 420, marginBottom: 14 }}>
      <Search
        size={16}
        style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#888780' }}
      />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Search by doctor name or speciality…"
        aria-label="Search doctors by name or speciality"
        style={{
          width: '100%',
          padding: '10px 36px 10px 36px',
          borderRadius: 10,
          border: '1px solid #E5E7EB',
          fontSize: 14,
          background: 'white',
          outline: 'none'
        }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            border: 'none', background: 'none', cursor: 'pointer', color: '#888780',
            padding: 4, display: 'flex'
          }}
        >
          <X size={15} />
        </button>
      )}
    </div>
  );
}


// ============================================================
// DATE STRIP
// ============================================================

function DateStrip({ dates, activeDate, onSelect }) {
  return (
    <div className="date-strip">
      {dates.map(date => (
        <button
          key={date}
          onClick={() => onSelect(date)}
          className={`date-chip ${activeDate === date ? 'active' : ''}`}
        >
          {formatDate(date)}
        </button>
      ))}
    </div>
  );
}


// ============================================================
// APPOINTMENT TYPE STEP
// ============================================================

function AppointmentTypeStep({ value, onSelect }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: 10,
        // Wide enough for all three options on one row; wraps on narrow screens.
        maxWidth: 580
      }}
    >
      {APPOINTMENT_TYPES.map(type => {
        const Icon = type.icon;
        const active = value === type.value;

        return (
          <button
            key={type.value}
            onClick={() => onSelect(type.value)}
            className={`slot-chip ${active ? 'active' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              height: 44,
              padding: '0 14px',
              whiteSpace: 'nowrap',
              fontSize: 14
            }}
          >
            <Icon size={16} style={{ flexShrink: 0 }} />
            {type.label}
          </button>
        );
      })}
    </div>
  );
}


// ============================================================
// BOOKING FLOW
// ============================================================

function BookingFlow({ onBooked }) {
  const myPhone = useMyPhoneDisplay();

  const [calendar, setCalendar] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [activeDate, setActiveDate] = useState(null);
  // the doctor's own zone label, as reported by the server with the slots
  const [doctorTzLabel, setDoctorTzLabel] = useState(null);
  // doctor covering the selected doctor on the selected date (from the server), or null
  const [coverDoctor, setCoverDoctor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [appointmentType, setAppointmentType] = useState(null);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  // The schedule panel under the selected doctor — scrolled into view on select
  const panelRef = useRef(null);

  const dates = calendar?.dates || [];
  const viewerTzLabel = calendar?.timezone_label || '';


  // ----------------------------------------------------------
  // Patient calendar (timezone + next 7 days) — from the server
  // ----------------------------------------------------------

  useEffect(() => {
    fetchCalendar()
      .then(cal => {
        setCalendar(cal);
        setActiveDate(cal.today);
      })
      .catch(err => setError(err.message));
  }, []);


  // ----------------------------------------------------------
  // Load doctors
  // ----------------------------------------------------------

  useEffect(() => {
    setLoadingDoctors(true);

    fetchDoctors()
      .then(rows => setDoctors(rows || []))
      .catch(err => setError(err.message))
      .finally(() => setLoadingDoctors(false));
  }, []);


  // ----------------------------------------------------------
  // Search — by name or speciality
  // ----------------------------------------------------------

  const filteredDoctors = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return doctors;

    return doctors.filter(doc =>
      String(doc.name || '').toLowerCase().includes(term) ||
      String(doc.specialty || '').toLowerCase().includes(term)
    );
  }, [doctors, search]);


  // ----------------------------------------------------------
  // Load slots
  // ----------------------------------------------------------

  const loadSlots = useCallback(() => {
    if (!selectedDoctor || !activeDate) {
      return;
    }

    setLoadingSlots(true);
    setSlots([]); // don't flash the previous doctor's times
    setCoverDoctor(null);
    setSelectedSlot(null);
    setAppointmentType(null);

    fetchSlots(selectedDoctor.id, activeDate)
      .then(({ data: rows, doctor_timezone_label, cover_doctor }) => {
        setDoctorTzLabel(doctor_timezone_label || null);
        setCoverDoctor(cover_doctor || null);
        setSlots(rows || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoadingSlots(false));
  }, [selectedDoctor, activeDate]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);


  // Bring the schedule (right under the chosen doctor) into view
  useEffect(() => {
    if (selectedDoctor && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedDoctor]);


  // ----------------------------------------------------------
  // Select / unselect a doctor
  // ----------------------------------------------------------

  function handleSelectDoctor(doctor) {
    setError(null);
    setSelectedSlot(null);
    setAppointmentType(null);

    // Clicking the open doctor again closes their schedule
    if (selectedDoctor?.id === doctor.id) {
      setSelectedDoctor(null);
      return;
    }

    setSelectedDoctor(doctor);
    if (calendar?.today) setActiveDate(calendar.today);
  }


  // ----------------------------------------------------------
  // Confirm booking
  // ----------------------------------------------------------

  async function handleConfirm() {
    if (!selectedDoctor || !selectedSlot || !appointmentType) {
      return;
    }

    setBooking(true);
    setError(null);

    try {
      const appointment = await bookAppointment(
        selectedDoctor.id,
        selectedSlot.id,
        appointmentType
      );

      onBooked(appointment);

      setSelectedDoctor(null);
      setSelectedSlot(null);
      setAppointmentType(null);
    } catch (err) {
      setError(err.message || 'Could not book appointment');
    } finally {
      setBooking(false);
    }
  }


  // ----------------------------------------------------------
  // Steps 2–4, shown directly under the selected doctor's card
  // ----------------------------------------------------------

  function renderSchedulePanel() {
    return (
      <div
        ref={panelRef}
        className="section-stack"
        style={{
          margin: '-4px 0 6px',
          padding: 18,
          border: '1px solid #F2C9A4',
          borderTop: 'none',
          borderRadius: '0 0 14px 14px',
          background: 'white',
          scrollMarginBottom: 24
        }}
      >
        <div>
          <h3 className="section-heading">
            2. Pick a date and time
          </h3>

          <p className="muted-text" style={{ margin: '0 0 10px', fontSize: 12.5 }}>
            Times are shown in your timezone:{' '}
            <strong>{viewerTzLabel}</strong>
            {doctorTzLabel && doctorTzLabel !== viewerTzLabel && (
              <>
                {' · '}
                {selectedDoctor.name} is in {doctorTzLabel}
              </>
            )}
          </p>

          <DateStrip
            dates={dates}
            activeDate={activeDate}
            onSelect={setActiveDate}
          />

          {!loadingSlots && coverDoctor && (
            <div
              role="status"
              style={{
                margin: '12px 0 0', padding: '10px 12px', borderRadius: 10,
                background: '#FFF4E8', border: '1px solid #F2C9A4', color: '#9A4A00', fontSize: 13
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700 }}>
                <UserCheck size={14} style={{ flexShrink: 0 }} />
                {selectedDoctor.name} is away on this date
              </div>
              <div style={{ marginTop: 4 }}>
                <strong>{coverDoctor.name}</strong>
                {coverDoctor.specialty ? ` (${coverDoctor.specialty})` : ''} is covering their appointments
                {' · '}
                {coverDoctor.start_date ? formatDate(coverDoctor.start_date) : 'Now'}
                {' → '}
                {coverDoctor.end_date ? formatDate(coverDoctor.end_date) : 'until further notice'}
              </div>
            </div>
          )}

          {loadingSlots ? (
            <p className="muted-text" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Loader2 size={14} className="spin" /> Loading available times…
            </p>
          ) : slots.length === 0 ? (
            <p className="muted-text" style={{ marginTop: 12 }}>
              {activeDate === calendar?.today
                ? 'No more open slots today — try another day.'
                : 'No open slots on this date — try another day.'}
            </p>
          ) : (
            <div className="slot-grid">
              {slots.map(slot => (
                <button
                  key={slot.id}
                  onClick={() => setSelectedSlot(slot)}
                  className={`slot-chip ${selectedSlot?.id === slot.id ? 'active' : ''}`}
                >
                  {formatTime(slot.slot_time)}
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedSlot && (
          <div>
            <h3 className="section-heading">
              3. Appointment type
            </h3>

            <AppointmentTypeStep
              value={appointmentType}
              onSelect={setAppointmentType}
            />
          </div>
        )}

        {selectedSlot && appointmentType && (
          <div className="confirm-bar">
            <div>
              <p className="confirm-title">
                {selectedDoctor.name}
              </p>

              <p className="confirm-meta">
                {formatDate(activeDate)}
                {' · '}
                {formatTime(selectedSlot.slot_time)}
                {' '}
                ({viewerTzLabel})
                {' · '}
                {appointmentTypeLabel(appointmentType)}
              </p>

              {coverDoctor && (
                <p className="confirm-meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <UserCheck size={12} style={{ flexShrink: 0 }} />
                  {coverDoctor.name} will be covering this appointment
                </p>
              )}

              {appointmentType === 'physical' &&
                formatLocation(selectedDoctor.address, selectedDoctor.country) && (
                <p className="confirm-meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <MapPin size={12} style={{ flexShrink: 0 }} />
                  {formatLocation(selectedDoctor.address, selectedDoctor.country)}
                </p>
              )}

              {appointmentType === 'zoom' && (
                <p className="confirm-meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Video size={12} style={{ flexShrink: 0 }} />
                  A Zoom link will be generated once you confirm
                </p>
              )}

              {appointmentType === 'phone' && (
                <p className="confirm-meta" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Phone size={12} style={{ flexShrink: 0 }} />
                  {myPhone
                    ? `The doctor will call you on ${myPhone}`
                    : 'You have no phone number saved — add one to your profile so the doctor can call you'}
                </p>
              )}
            </div>
            
            <button
              onClick={handleConfirm}
              disabled={booking}
              className="btn-primary"
            >
              {booking ? 'Booking…' : 'Confirm appointment'}
            </button>
          </div>
        )}

        {error && (
          <p className="error-text">
            {error}
          </p>
        )}
      </div>
    );
  }


  return (
    <div className="section-stack">
      <style>{`.spin { animation: appt-spin 1s linear infinite; } @keyframes appt-spin { to { transform: rotate(360deg); } }`}</style>

      <div>
        <h3 className="section-heading">
          1. Choose a doctor
        </h3>

        <DoctorSearch value={search} onChange={setSearch} />

        {loadingDoctors ? (
          <p className="muted-text">
            Loading doctors…
          </p>
        ) : doctors.length === 0 ? (
          <p className="muted-text">
            No doctors available right now.
          </p>
        ) : filteredDoctors.length === 0 ? (
          <p className="muted-text">
            No doctors match “{search}”. Try another name or speciality.
          </p>
        ) : (
          <div className="doctor-grid">
            {filteredDoctors.map(doctor => {
              const isSelected = selectedDoctor?.id === doctor.id;

              return (
                <React.Fragment key={doctor.id}>
                  <DoctorCard
                    doctor={doctor}
                    selected={isSelected}
                    onClick={() => handleSelectDoctor(doctor)}
                  />

                  {/* Date, time and type open right under the chosen doctor */}
                  {isSelected && renderSchedulePanel()}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* Errors not tied to an open doctor (e.g. doctors failed to load) */}
      {error && !selectedDoctor && (
        <p className="error-text">
          {error}
        </p>
      )}
    </div>
  );
}


// ============================================================
// APPOINTMENT ROW
// ============================================================

function AppointmentRow({ appt, onCancel, cancelling }) {
  const isUpcoming = appt.status === 'confirmed';
  const location = formatLocation(appt.doctor_address, appt.doctor_country);
  const isZoom = appt.appointment_type === 'zoom';
  const isPhone = appt.appointment_type === 'phone';
  const myPhone = useMyPhoneDisplay();

  return (
    <div className="appt-card">
      <div className="appt-card-left">
        <div className="appt-icon">
          <Stethoscope size={16} />
        </div>

        <div>
          <p className="appt-doctor">
            {appt.doctor_name}
          </p>

          <p className="appt-specialty">
            {appt.doctor_specialty}
          </p>

          {isZoom ? (
            <p className="appt-specialty" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Video size={12} style={{ flexShrink: 0 }} />
              Zoom video call
            </p>
          ) : isPhone ? (
            <p className="appt-specialty" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Phone size={12} style={{ flexShrink: 0 }} />
              {isUpcoming && myPhone
                ? `Phone call · the doctor will call you on ${myPhone}`
                : 'Phone call · the doctor will call you'}
            </p>
          ) : (
            location && (
              <p className="appt-specialty" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <MapPin size={12} style={{ flexShrink: 0 }} />
                {location}
              </p>
            )
          )}

          <p className="appt-datetime">
            <Calendar size={12} />
            {formatDate(appt.appointment_date)}

            <Clock size={12} style={{ marginLeft: 10 }} />
            {formatTime(appt.appointment_time)}

            {appt.timezone_label && (
              <span style={{ marginLeft: 6, opacity: 0.75 }}>
                {appt.timezone_label}
              </span>
            )}
          </p>

          {appt.cover_doctor && appt.status !== 'cancelled' && (
            <p
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                margin: '8px 0 0', padding: '5px 10px', borderRadius: 8,
                background: '#FFF4E8', border: '1px solid #F2C9A4', color: '#9A4A00', fontSize: 12.5
              }}
            >
              <UserCheck size={13} style={{ flexShrink: 0 }} />
              <span>
                {appt.doctor_name} is away — covered by <strong>{appt.cover_doctor.name}</strong>
                {appt.cover_doctor.specialty ? ` (${appt.cover_doctor.specialty})` : ''}
              </span>
              <span style={{ opacity: 0.85 }}>
                · {appt.cover_doctor.start_date ? formatDate(appt.cover_doctor.start_date) : 'Now'}
                {' → '}
                {appt.cover_doctor.end_date ? formatDate(appt.cover_doctor.end_date) : 'until further notice'}
              </span>
            </p>
          )}

          {isZoom && appt.meeting_link && appt.status !== 'cancelled' && (
            <a
              href={appt.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="appt-followup"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Video size={12} />
              Join Zoom meeting
            </a>
          )}

          {appt.status === 'completed' && appt.follow_up_plan && (
            <p className="appt-followup">
              Follow-up:{' '}{appt.follow_up_plan}
            </p>
          )}
        </div>
      </div>

      <div className="appt-card-right">
        <span className={`status-pill status-${appt.status}`}>
          {appt.status}
        </span>

        {isUpcoming && (
          <button
            onClick={() => onCancel(appt.id)}
            disabled={cancelling === appt.id}
            className="cancel-btn"
          >
            <XCircle size={13} />
            {cancelling === appt.id ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
      </div>
    </div>
  );
}


// ============================================================
// MY APPOINTMENTS
// ============================================================

function MyAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('upcoming');
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);

    fetchAppointments()
      .then(setAppointments)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCancel(id) {
    setCancelling(id);

    try {
      await cancelAppointment(id);

      setAppointments(prev =>
        prev.map(appt =>
          appt.id === id
            ? { ...appt, status: 'cancelled' }
            : appt
        )
      );
    } catch (err) {
      setError(err.message || 'Could not cancel appointment');
    } finally {
      setCancelling(null);
    }
  }

  const filtered = appointments.filter(appt => {
    // The server says whether the appointment's moment has passed.
    const isPast = Boolean(appt.is_past);

    if (filter === 'upcoming') {
      return appt.status === 'confirmed' && !isPast;
    }

    return appt.status !== 'confirmed' || isPast;
  });

  return (
    <div className="section-stack">
      <div className="timeline-filters">
        <button
          onClick={() => setFilter('upcoming')}
          className={`filter-chip ${filter === 'upcoming' ? 'active' : ''}`}
        >
          Upcoming
        </button>

        <button
          onClick={() => setFilter('past')}
          className={`filter-chip ${filter === 'past' ? 'active' : ''}`}
        >
          Past &amp; cancelled
        </button>
      </div>

      {loading && (
        <p className="muted-text">
          Loading appointments…
        </p>
      )}

      {!loading && error && (
        <p className="error-text">
          {error}
        </p>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p className="muted-text">
          No {filter} appointments.
        </p>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="appt-list">
          {filtered.map(appt => (
            <AppointmentRow
              key={appt.id}
              appt={appt}
              onCancel={handleCancel}
              cancelling={cancelling}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
// MAIN APPOINTMENTS
// ============================================================

const Appointments = () => {
  const [tab, setTab] = useState('Book appointment');
  const [refreshKey, setRefreshKey] = useState(0);

  function handleBooked() {
    setTab('My appointments');
    setRefreshKey(key => key + 1);
  }

  return (
    <div className="appt-page">
      <div className="appt-header">
        <div className="appt-header-inner">
          <div>
            <h1 className="appt-title">
              Appointments
            </h1>

            <p className="appt-subtitle">
              Book a consultation and track your visits
            </p>
          </div>
        </div>
      </div>

      <div className="appt-container">
        <div className="health-tabs">
          {TABS.map(tabName => (
            <button
              key={tabName}
              onClick={() => setTab(tabName)}
              className={`health-tab ${tab === tabName ? 'active' : ''}`}
            >
              {tabName}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {tab === 'Book appointment' && (
            <BookingFlow onBooked={handleBooked} />
          )}

          {tab === 'My appointments' && (
            <MyAppointments key={refreshKey} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Appointments;